import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import type { EmailLogListItem } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useCan } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { EmailStatusCell } from '@/components/email-status-cell';
import { Section } from '@/components/detail-layout';
import { MailIcon } from '@/components/layout/icons';

const NS = 'pages.admin.members.detail.emails';
const LIMIT = 20;

type MemberEmailsSectionProps = { readonly email: string; readonly workspaceId: string };

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly rows: ReadonlyArray<EmailLogListItem> }
  | { readonly status: 'failed' };

export function MemberEmailsSection({ email, workspaceId }: MemberEmailsSectionProps) {
  const t = useTranslations(NS);
  const tLog = useTranslations('pages.admin.email-log');
  const intl = useIntl();
  const canRead = useCan('backoffice:identity:audit-event-read');
  const [state, setState] = useState<State>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!canRead) return;

    let active = true;

    setState({ status: 'loading' });
    apiClient.adminEmailLog.list({ query: { recipient: email, workspaceId, limit: LIMIT } }).then(
      (result) => {
        if (active) setState(result.ok ? { status: 'loaded', rows: result.body.data } : { status: 'failed' });
      },
      () => {
        if (active) setState({ status: 'failed' });
      },
    );

    return () => {
      active = false;
    };
  }, [canRead, email, workspaceId, attempt]);

  if (!canRead) return null;

  return (
    <Section collapsible title={t('title')} icon={MailIcon}>
      {state.status === 'loading' && <p className="text-text-tertiary">{t('loading')}</p>}
      {state.status === 'failed' && (
        <div className="flex items-center gap-3">
          <p role="alert" className="text-text-tertiary">
            {t('error')}
          </p>
          <Button size="sm" variant="secondary" type="button" onClick={() => setAttempt((n) => n + 1)}>
            {t('retry')}
          </Button>
        </div>
      )}
      {state.status === 'loaded' && state.rows.length === 0 && <p className="text-text-tertiary">{t('empty')}</p>}
      {state.status === 'loaded' && state.rows.length > 0 && (
        <Table flush>
          <TableHeader>
            <TableRow>
              <TableHead>{t('columns.kind')}</TableHead>
              <TableHead>{t('columns.subject')}</TableHead>
              <TableHead className="w-[150px] whitespace-nowrap">{t('columns.sent-at')}</TableHead>
              <TableHead className="w-[220px]">{t('columns.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {state.rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{tLog(`kinds.${row.kind}`)}</TableCell>
                <TableCell className="max-w-0">
                  <div className="truncate" title={row.subject}>
                    {row.subject}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-text-tertiary tabular-nums">
                  {intl.formatDate(row.sentAt, { dateStyle: 'short', timeStyle: 'short' })}
                </TableCell>
                <TableCell>
                  <EmailStatusCell status={row.status} statusAt={row.statusAt} failureReason={row.failureReason} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}
